// Home — the search-first landing page, the React port of the PHP
// portal's index.php ("Search"). A big centered lookup box with a live
// autocomplete (GET /v1/api/suggest), a recent-searches list, the
// four-up network tile strip, and an empty-state hint. Picking a result
// (or pressing Enter) renders the subscriber card **in place** below the
// box — same as the PHP page — and hides the tiles/hint until you go
// back. (The `/subscribers/:username` route still exists for deep links.)

import {
  lazy,
  Suspense,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Cpu, Network, Phone, Search, User, X } from "lucide-react";
import { Card, Input, Spinner, cn } from "@noc/ui";
import { api } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { useDebounced } from "@/lib/useDebounced";
import { useSseStatus } from "@/lib/sse";
import { subscriberState, stateDotClass } from "@/lib/subscriberState";
import { useCan, useRole } from "@/features/auth/roles";
import { useAuth } from "@/features/auth/AuthContext";
import { FleetTilesStrip } from "@/features/fleet/FleetTilesStrip";
import { SwitchPicker } from "@/features/switches/SwitchPicker";
import { useRecentSearches } from "./useRecentSearches";
import type { SuggestMatch } from "@noc/api";

// The subscriber detail subtree (RHF/Zod + every action dialog + the
// cards) is heavy — load it on demand, the same chunk the /subscribers/
// :username route uses. Until you pick someone, the home page is light.
const SubscriberDetailPage = lazy(() =>
  import("@/features/subscribers/SubscriberDetailPage").then((m) => ({ default: m.SubscriberDetailPage })),
);

type Mode = "user" | "switch";

export function HomePage() {
  // The network tile strip: admin/support always; a reseller only once an
  // admin has granted it routers / OLTs — then the tiles are server-scoped
  // to that grant (dashboard.apiTiles reads the same Caller).
  const canDashboard = useCan("dashboard:read");
  const canOltsRead = useCan("olts:read");
  const canRoutersRead = useCan("routers:read");
  const canSeeTiles = canDashboard || canOltsRead || canRoutersRead;
  const isReseller = useRole() === "reseller";
  const resellerName = useAuth().user?.reseller_name;
  const [mode, setMode] = useState<Mode>("user");
  // The subscriber whose card is currently shown inline (PHP renders the
  // result on the same page). null = nothing searched yet → tiles + hint.
  const [selected, setSelected] = useState<string | null>(null);

  function switchMode(m: Mode) {
    setMode(m);
    if (m === "switch") setSelected(null); // PHP clears the result on mode change
  }

  return (
    <div className="space-y-4 pt-2">
      {isReseller && (
        <div className="mx-auto w-full max-w-2xl rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm text-muted-foreground">
          Showing only <span className="font-medium text-foreground">{resellerName || "your reseller"}</span>’s subscribers.
        </div>
      )}
      <div className="mx-auto w-full max-w-2xl">
        {/* Switch mode works for resellers too — /v1/api/switches/{list,users}
            are reseller-scoped server-side, so the picker shows only their
            customers' switches and rosters. */}
        <ModeTabs mode={mode} onChange={switchMode} />
        {mode === "user" ? <UserSearch onSelect={setSelected} /> : <SwitchPicker />}
      </div>

      {mode === "user" &&
        (selected ? (
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-16">
                <Spinner />
              </div>
            }
          >
            <SubscriberDetailPage key={selected} username={selected} onBack={() => setSelected(null)} />
          </Suspense>
        ) : (
          <div className="mx-auto w-full max-w-2xl">
            {canSeeTiles && <FleetTilesStrip />}
            <EmptyHint />
          </div>
        ))}
    </div>
  );
}

// ── Mode tabs (User / Switch) ──
function ModeTabs({ mode, onChange }: { mode: Mode; onChange: (m: Mode) => void }) {
  const tabs: { key: Mode; label: string; icon: typeof User }[] = [
    { key: "user", label: "User", icon: User },
    { key: "switch", label: "Switch", icon: Network },
  ];
  return (
    <div className="mb-3 inline-flex rounded-md border bg-card p-0.5">
      {tabs.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          aria-pressed={mode === key}
          className={cn(
            "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors",
            mode === key
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          )}
        >
          <Icon className="size-4" />
          {label}
        </button>
      ))}
    </div>
  );
}

// ── User search box + autocomplete + recents ──
function UserSearch({ onSelect }: { onSelect: (username: string) => void }) {
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);
  // Recents only drop down when focus came via the "/" shortcut — not on
  // a plain click / autofocus (a click should give you a quiet box).
  const [recentsOpen, setRecentsOpen] = useState(false);
  const focusViaKeyRef = useRef(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const q = useDebounced(input.trim(), 180);
  const sse = useSseStatus(); // "idle" | "connecting" | "open" | "error"
  const { recent, push: pushRecent, remove: removeRecent } = useRecentSearches();

  const { data, isFetching } = useQuery({
    queryKey: qk.suggest(q),
    queryFn: () => api.search.suggest(q),
    enabled: q.length >= 2,
    staleTime: 15_000,
  });
  const matches: SuggestMatch[] = data?.ok ? data.matches : [];

  // Which list keyboard nav drives: suggestions when querying, otherwise recents.
  const showingSuggest = q.length >= 2;
  const navItems: string[] = showingSuggest ? matches.map((m) => m.username) : recent;
  const open = focused && (showingSuggest ? matches.length > 0 : recentsOpen && recent.length > 0);

  useEffect(() => setActiveIdx(0), [q, showingSuggest]);

  function go(username: string) {
    const u = username.trim();
    if (!u) return;
    setInput(u); // reflect the picked subscriber in the box (PHP does this)
    pushRecent(u);
    setFocused(false);
    inputRef.current?.blur(); // dismiss the mobile keyboard
    onSelect(u);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const picked = open && navItems[activeIdx] ? navItems[activeIdx] : input;
    go(picked);
  }

  function onKeyDown(e: KeyboardEvent) {
    if (!open || navItems.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % navItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + navItems.length) % navItems.length);
    } else if (e.key === "Escape") {
      setFocused(false);
    } else if (e.key === "Enter") {
      // Only intercept Enter when something is highlighted; otherwise let
      // the form submit (which falls back to the typed value).
      if (navItems[activeIdx]) {
        e.preventDefault();
        go(navItems[activeIdx]);
      }
    }
  }

  // Global "/" shortcut: focus the search box + drop down recents. When
  // the box is *already* focused (it autofocuses on page load), "/"
  // still opens recents instead of typing a literal slash. Pressing "/"
  // while typing in some *other* field is left alone (literal char).
  useEffect(() => {
    function onDocKey(e: globalThis.KeyboardEvent) {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (el === inputRef.current) {
        e.preventDefault();
        setRecentsOpen(true);
        return;
      }
      if (el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) return;
      e.preventDefault();
      focusViaKeyRef.current = true; // tell onFocus this came from "/" → show recents
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    document.addEventListener("keydown", onDocKey);
    return () => document.removeEventListener("keydown", onDocKey);
  }, []);

  return (
    <form onSubmit={onSubmit} className="relative" role="search">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => {
            setFocused(true);
            setRecentsOpen(focusViaKeyRef.current); // true only when "/" brought us here
            focusViaKeyRef.current = false;
          }}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={onKeyDown}
          placeholder="Username · Phone number · MAC"
          autoComplete="off"
          autoFocus
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          className="h-12 pl-10 pr-3 font-mono text-base"
        />
      </div>

      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <span>{isFetching ? "Searching…" : ""}</span>
        <span className="ml-auto flex items-center gap-2">
          <span
            className={cn(
              "inline-block size-2 rounded-full",
              sse === "open"
                ? "bg-success"
                : sse === "error"
                  ? "bg-destructive"
                  : "bg-muted-foreground/40",
            )}
            title={
              sse === "open"
                ? "Live data — connected"
                : sse === "error"
                  ? "Live data — connection error"
                  : sse === "connecting"
                    ? "Live data — connecting…"
                    : "Live data — idle (nothing to watch on this page)"
            }
          />
          <span className="hidden sm:inline">
            Press <kbd className="rounded border bg-muted px-1 font-sans">/</kbd> to focus
          </span>
        </span>
      </div>

      {open && (
        <Card id={listboxId} role="listbox" className="absolute inset-x-0 z-20 mt-1 max-h-80 overflow-auto p-1">
          {showingSuggest
            ? matches.map((m, i) => {
                const st = subscriberState(m.status, m.address);
                const meta = [m.mobile, m.switch, m.router].filter(Boolean).join(" · ");
                return (
                  <button
                    key={`${m.username}-${i}`}
                    type="button"
                    role="option"
                    aria-selected={i === activeIdx}
                    onMouseEnter={() => setActiveIdx(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => go(m.username)}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                      i === activeIdx ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
                    )}
                  >
                    <span className={cn("size-2 shrink-0 rounded-full", stateDotClass(st))} />
                    <span className="shrink-0 font-mono font-medium">{m.username}</span>
                    <span className="ml-auto truncate text-xs text-muted-foreground">{meta}</span>
                  </button>
                );
              })
            : recent.map((qq, i) => (
                <div
                  key={qq}
                  role="option"
                  aria-selected={i === activeIdx}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors",
                    i === activeIdx ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
                  )}
                >
                  <span className="size-2 shrink-0 rounded-full bg-muted-foreground/40" />
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIdx(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => go(qq)}
                    className="flex-1 truncate text-left font-mono font-medium"
                  >
                    {qq}
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${qq} from recent`}
                    title="Remove"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => removeRecent(qq)}
                    className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ))}
          {!showingSuggest && recent.length > 0 && (
            <div className="px-3 pb-1 pt-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              Recent searches
            </div>
          )}
        </Card>
      )}

      {showingSuggest && !isFetching && matches.length === 0 && (
        <p className="mt-2 text-sm text-muted-foreground">
          No matches for “{q}”. Press Enter to open it as a username anyway.
        </p>
      )}
    </form>
  );
}

// ── Empty-state hint ──
function EmptyHint() {
  const tags = [
    { icon: User, label: "Username" },
    { icon: Phone, label: "01XXXXXXXXX" },
    { icon: Cpu, label: "AA:BB:CC…" },
  ];
  return (
    <div className="mt-10 flex flex-col items-center gap-2 text-center">
      <span className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Search className="size-7" />
      </span>
      <p className="text-base font-medium">Search for a subscriber</p>
      <p className="text-sm text-muted-foreground">Look up by username, phone number, or MAC address</p>
      <div className="mt-1 flex flex-wrap justify-center gap-2">
        {tags.map(({ icon: Icon, label }) => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-xs text-muted-foreground"
          >
            <Icon className="size-3.5" />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
